import { getDatabase, ref, get, set, push } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Domain Helper Functions für Custom Domain System
 */

/**
 * Debug-Tool: Teste Domain-Erkennung manuell
 */
export const debugDomainDetection = (testDomain = null) => {
  const hostname = testDomain || window.location.hostname;
  
  const excludedDomains = [
    'bonusclub.link',
    'localhost',
    '127.0.0.1',
    'bonusclub-dev.web.app',
    'bonusclub-ab5e2.web.app'
  ];
  
  const matchedExclusion = excludedDomains.find(excluded => hostname.includes(excluded));
  const isCustom = !matchedExclusion;
  
  const result = {
    hostname,
    isCustomDomain: isCustom,
    matchedExclusion: matchedExclusion || 'none',
    excludedDomains,
    explanation: isCustom 
      ? '✅ This is a CUSTOM DOMAIN' 
      : `❌ This is NOT a custom domain (matched: ${matchedExclusion})`
  };
  
  console.table(result);
  return result;
};

/**
 * Prüft ob aktuelle Domain eine Custom Domain ist
 */
export const isCustomDomain = () => {
  const hostname = window.location.hostname;
  
  // 🔥 NEW: Check for proxy headers (when accessed via Cloudflare Worker)
  // Method 1: Check URL parameters
  const originalHostFromUrl = new URLSearchParams(window.location.search).get('__bc_original_host');
  
  // Method 2: Check if we're being proxied (different hostname in URL vs actual)
  const isProxiedRequest = window.location.hostname === 'bonusclub.link' && 
                          window.location.pathname.startsWith('/to/');
  
  // Method 3: Check global variable set by worker
  const originalHost = window.__BC_ORIGINAL_HOST__ || 
                      document.querySelector('meta[name="x-original-host"]')?.content ||
                      originalHostFromUrl;
  
  if (originalHost) {
    console.log('🌐 Detected proxy request - Original Host:', originalHost);
    
    // Check if original host is a custom domain
    const excludedDomains = [
      'bonusclub.link',
      'localhost',
      '127.0.0.1',
      'bonusclub-dev.web.app',
      'bonusclub-ab5e2.web.app'
    ];
    
    const isOriginalCustom = !excludedDomains.some(excluded => originalHost.includes(excluded));
    
    console.log('🔍 isCustomDomain() proxy check:', {
      hostname,
      originalHost,
      isOriginalCustom,
      source: 'proxy-header'
    });
    
    return isOriginalCustom;
  }
  
  // Standard check for direct access
  const excludedDomains = [
    'bonusclub.link',
    'localhost',
    '127.0.0.1',
    'bonusclub-dev.web.app',
    'bonusclub-ab5e2.web.app'
  ];
  
  const isCustom = !excludedDomains.some(excluded => hostname.includes(excluded));
  
  return isCustom;
};

/**
 * Escapes domain name for Firebase path (replaces dots with underscores)
 */
export const escapeDomainForFirebase = (domain) => {
  return domain.replace(/\./g, '_DOT_').replace(/[#$\[\]]/g, '_');
};

/**
 * Unescapes Firebase path back to domain name
 */
export const unescapeDomainFromFirebase = (escapedDomain) => {
  return escapedDomain.replace(/_DOT_/g, '.').replace(/_/g, '');
};

/**
 * Escapes page path for Firebase key (replaces slashes and other invalid chars)
 */
export const escapePathForFirebase = (path) => {
  return path.replace(/\//g, '_SLASH_')
             .replace(/\./g, '_DOT_')
             .replace(/[#$\[\]]/g, '_');
};

/**
 * Unescapes Firebase key back to page path
 */
export const unescapePathFromFirebase = (escapedPath) => {
  return escapedPath.replace(/_SLASH_/g, '/')
                   .replace(/_DOT_/g, '.')
                   .replace(/_/g, '');
};

/**
 * Lädt Domain-Mapping für eine Custom Domain
 */
export const getDomainMapping = async (domain) => {
  try {
    const db = getDatabase();
    const escapedDomain = escapeDomainForFirebase(domain);
    
    // 🔥 FIX: Try Cloudflare mappings FIRST (new system)
    const cloudflareRef = ref(db, `/cloudflare/mappings/${escapedDomain}`);
    const cloudflareSnapshot = await get(cloudflareRef);
    
    if (cloudflareSnapshot.exists()) {
      const data = cloudflareSnapshot.val();
      console.log('✅ getDomainMapping: Loaded from /cloudflare/mappings/', { domain, data });
      // Add the original domain to the returned data
      return {
        ...data,
        originalDomain: domain,
        source: 'cloudflare'
      };
    }
    
    // Fallback: Try legacy domainMappings
    const domainRef = ref(db, `/domainMappings/${escapedDomain}`);
    const snapshot = await get(domainRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      console.log('⚠️ getDomainMapping: Loaded from /domainMappings/ (legacy)', { domain, data });
      // Add the original domain to the returned data
      return {
        ...data,
        originalDomain: domain,
        source: 'legacy'
      };
    }
    
    console.log('❌ getDomainMapping: No mapping found for domain:', domain);
    return null;
  } catch (error) {
    console.error('Error loading domain mapping:', error);
    return null;
  }
};

/**
 * Erstellt neues Domain-Mapping
 */
export const createDomainMapping = async (domain, userId, pages = {}, settings = {}) => {
  try {
    const db = getDatabase();
    const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
    
    // Prüfe ob Domain bereits existiert
    const existingMapping = await getDomainMapping(normalizedDomain);
    if (existingMapping) {
      throw new Error('Domain already exists');
    }

    // Convert pages object to escaped format
    const escapedPages = {};
    Object.keys(pages).forEach(path => {
      const escapedPath = escapePathForFirebase(path);
      escapedPages[escapedPath] = {
        originalPath: path,
        pageId: pages[path]
      };
    });

    const domainMapping = {
      userId,
      domain: normalizedDomain, // Store original domain
      pages: escapedPages, // Escaped page mappings
      settings: {
        siteName: settings.siteName || domain,
        favicon: settings.favicon || '',
        appleTouchIcon: settings.appleTouchIcon || '',
        maskIcon: settings.maskIcon || '',
        brandColor: settings.brandColor || '#ffffff',
        logoUrl: settings.logoUrl || '',
        ogImage: settings.ogImage || '',
        defaultPage: settings.defaultPage || '/'
      },
      domainStatus: 'pending', // pending|verified|error
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const escapedDomain = escapeDomainForFirebase(normalizedDomain);
    const domainRef = ref(db, `/domainMappings/${escapedDomain}`);
    await set(domainRef, domainMapping);
    
    return domainMapping;
  } catch (error) {
    console.error('Error creating domain mapping:', error);
    throw error;
  }
};

/**
 * Aktualisiert Domain-Mapping
 */
export const updateDomainMapping = async (domain, updates) => {
  try {
    const db = getDatabase();
    const escapedDomain = escapeDomainForFirebase(domain);
    const domainRef = ref(db, `/domainMappings/${escapedDomain}`);
    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await set(domainRef, updatedData);
    return updatedData;
  } catch (error) {
    console.error('Error updating domain mapping:', error);
    throw error;
  }
};

/**
 * Lädt alle Domain-Mappings für einen User
 */
export const getUserDomainMappings = async (userId) => {
  try {
    const db = getDatabase();
    const mappingsRef = ref(db, '/domainMappings');
    const snapshot = await get(mappingsRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const allMappings = snapshot.val();
    const userMappings = [];
    
    Object.keys(allMappings).forEach(escapedDomain => {
      const mapping = allMappings[escapedDomain];
      if (mapping.userId === userId) {
        userMappings.push({
          domain: mapping.domain || unescapeDomainFromFirebase(escapedDomain), // Use stored domain or unescape
          ...mapping
        });
      }
    });
    
    return userMappings;
  } catch (error) {
    console.error('Error loading user domain mappings:', error);
    return [];
  }
};

/**
 * Löst Page-Pfad für Custom Domain auf
 */
export const resolvePageForDomain = async (domain, path = '/') => {
  try {
    const domainMapping = await getDomainMapping(domain);
    
    if (!domainMapping) {
      return null;
    }
    
    // 🔥 FIX: Support old format without pages object
    if (!domainMapping.pages || typeof domainMapping.pages !== 'object') {
      console.warn('⚠️ Old domain mapping format detected (no pages object). This domain needs to be migrated.', {
        domain,
        mapping: domainMapping
      });
      return null;
    }
    
    // Finde Page für Pfad - prüfe sowohl escaped als auch original Pfade
    let pageMapping = null;
    const escapedPath = escapePathForFirebase(path);
    
    // Versuche zuerst escaped path
    if (domainMapping.pages[escapedPath]) {
      pageMapping = domainMapping.pages[escapedPath];
    } else {
      // Fallback: Suche nach originalPath in allen Mappings
      const mappingEntries = Object.values(domainMapping.pages);
      pageMapping = mappingEntries.find(mapping => 
        mapping.originalPath === path || 
        (typeof mapping === 'string' && mapping === path) // Legacy support
      );
    }
    
    // Fallback zu default page
    if (!pageMapping && domainMapping.settings && domainMapping.settings.defaultPage) {
      const defaultEscapedPath = escapePathForFirebase(domainMapping.settings.defaultPage);
      pageMapping = domainMapping.pages[defaultEscapedPath];
    }
    
    if (!pageMapping) {
      return null;
    }
    
    // Extract pageId (handle both new object format and legacy string format)
    const pageId = pageMapping.pageId || pageMapping;
    
    if (!pageId) {
      return null;
    }
    
    // Lade Page-Daten from LIVE PAGES for Custom Domains
    const db = getDatabase();
    const livePageRef = ref(db, `/livePages/${pageId}`);
    const livePageSnapshot = await get(livePageRef);
    
    if (livePageSnapshot.exists()) {
      console.log("✅ Custom Domain: Page loaded from livePages:", pageId);
      return {
        pageData: livePageSnapshot.val(),
        pageId,
        domainMapping
      };
    }
    
    // Fallback: Check staging pages for backward compatibility
    console.log("⚠️ Custom Domain: Page not found in livePages, checking staging...");
    const stagingPageRef = ref(db, `/pages/${pageId}`);
    const stagingPageSnapshot = await get(stagingPageRef);
    
    if (stagingPageSnapshot.exists()) {
      const stagingData = stagingPageSnapshot.val();
      
      // Check both new system (isPublished) and old system (isActive) for backward compatibility
      if (stagingData.isPublished || stagingData.isActive) {
        console.log("⚠️ Custom Domain: Using staging data (published/active page)");
        return {
          pageData: stagingData,
          pageId,
          domainMapping
        };
      } else {
        console.log("❌ Custom Domain: Staging page exists but is not published/active");
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error resolving page for domain:', error);
    return null;
  }
};

/**
 * Validiert Domain-Namen
 */
export const validateDomain = (domain) => {
  // Entferne Protokoll falls vorhanden
  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase();
  
  // Basic Domain Validation
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
  
  if (!domainRegex.test(cleanDomain)) {
    return { valid: false, error: 'Invalid domain format' };
  }
  
  // Prüfe auf reservierte Domains
  const reservedDomains = ['bonusclub.link', 'localhost', '127.0.0.1'];
  if (reservedDomains.some(reserved => cleanDomain.includes(reserved))) {
    return { valid: false, error: 'Reserved domain' };
  }
  
  return { valid: true, domain: cleanDomain };
};

/**
 * Generiert Default-Settings für neue Domain
 */
export const generateDefaultDomainSettings = (domain, siteName) => {
  return {
    siteName: siteName || domain,
    favicon: '',
    appleTouchIcon: '',
    maskIcon: '',
    brandColor: '#ffffff',
    logoUrl: '',
    ogImage: '',
    defaultPage: '/'
  };
};

/**
 * SPACE-DOMAIN FUNCTIONS - Neue 1:1 Verbindung zwischen Space und Domain
 */

/**
 * Prüft ob Domain bereits existiert (Legacy + Cloudflare)
 */
export const checkDomainExists = async (domain) => {
  try {
    // 1. Prüfe Legacy Domain-Mappings
    const legacyMapping = await getDomainMapping(domain);
    if (legacyMapping) {
      return { exists: true, type: 'legacy', mapping: legacyMapping };
    }
    
    // 2. Prüfe Cloudflare-Mappings
    const cloudflareMapping = await getCloudflareDomainMapping(domain);
    if (cloudflareMapping) {
      return { exists: true, type: 'cloudflare', mapping: cloudflareMapping };
    }
    
    return { exists: false };
  } catch (error) {
    console.error('Error checking domain existence:', error);
    return { exists: false };
  }
};

/**
 * Verbindet eine Custom Domain mit einem Space
 */
export const connectDomainToSpace = async (userId, spaceId, domain, spaceName, username) => {
  try {
    const db = getDatabase();
    const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
    
    // Prüfe ob Domain bereits existiert (Legacy + Cloudflare)
    const domainCheck = await checkDomainExists(normalizedDomain);
    if (domainCheck.exists) {
      // Prüfe ob es die gleiche User/Space Kombination ist
      const existingMapping = domainCheck.mapping;
      if (existingMapping.userId === userId && existingMapping.spaceId === spaceId) {
        console.log('🔄 Domain already connected to this space, updating...');
        // Domain ist bereits mit diesem Space verbunden, das ist OK
        return existingMapping;
      } else {
        throw new Error(`Domain already exists and is connected to a different space (${domainCheck.type} system)`);
      }
    }

    // Validiere Domain
    const validation = validateDomain(normalizedDomain);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Lade alle Pages des Spaces für automatisches Mapping
    const spacePages = await getSpacePages(userId, spaceId);
    const pageMapping = {};
    
    // Automatisches Page-Mapping basierend auf Space-Pages
    // WICHTIG: Custom Domains verwenden /to/ Präfix um Konflikte zu vermeiden
    spacePages.forEach(page => {
      if (page.isHomePage || page.slug === "/") {
        // Home Page: domain.com/to/username/ 
        const originalPath = `/to/${username}/`;
        const escapedPath = escapePathForFirebase(originalPath);
        pageMapping[escapedPath] = {
          originalPath: originalPath,
          pageId: `${userId}/${spaceId}/home`
        };
      } else {
        // Sub Pages: domain.com/to/username/pagename
        const slug = page.slug || page.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const originalPath = `/to/${username}/${slug}`;
        const escapedPath = escapePathForFirebase(originalPath);
        pageMapping[escapedPath] = {
          originalPath: originalPath,
          pageId: `${userId}/${spaceId}/${page.pageName || slug}`
        };
      }
    });

    // Erstelle Domain-Mapping mit Space-Verbindung
    const domainMapping = {
      userId,
      spaceId,           // NEU: Direkte Space-Verbindung
      username,          // NEU: Für schnelles Routing
      domain: normalizedDomain, // Store original domain
      pages: pageMapping,
      settings: {
        siteName: spaceName || normalizedDomain,
        favicon: '',
        appleTouchIcon: '',
        maskIcon: '',
        brandColor: '#ffffff',
        logoUrl: '',
        ogImage: '',
        defaultPage: '/'
      },
      domainStatus: 'pending', // pending|verified|error
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Speichere Domain-Mapping
    const escapedDomain = escapeDomainForFirebase(normalizedDomain);
    const domainRef = ref(db, `/domainMappings/${escapedDomain}`);
    await set(domainRef, domainMapping);

    // Update Space mit Domain-Info
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    const spaceSnapshot = await get(spaceRef);
    
    if (spaceSnapshot.exists()) {
      const spaceData = spaceSnapshot.val();
      await set(spaceRef, {
        ...spaceData,
        customDomain: normalizedDomain,
        domainStatus: 'pending',
        updatedAt: new Date().toISOString()
      });
    }

    console.log('✅ Domain connected to space:', { domain: normalizedDomain, spaceId, username });
    
    return domainMapping;
  } catch (error) {
    console.error('Error connecting domain to space:', error);
    throw error;
  }
};

/**
 * Lädt alle Pages eines Spaces
 */
export const getSpacePages = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    
    // Load from staging pages for domain setup/management
    // (Domain management should see all pages, not just published ones)
    const spacePagesRef = ref(db, `/pages/${userId}/${spaceId}`);
    const snapshot = await get(spacePagesRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const pagesData = snapshot.val();
    const pages = [];
    
    Object.keys(pagesData).forEach(pageName => {
      const page = pagesData[pageName];
      pages.push({
        pageName,
        ...page,
        id: `${userId}/${spaceId}/${pageName}`
      });
    });
    
    // Sortiere Pages nach Erstellungsdatum (neueste zuerst)
    const sortedPages = pages.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.updatedAt || 0);
        return dateB - dateA; // Neueste zuerst (descending)
    });
    
    return sortedPages;
  } catch (error) {
    console.error('Error loading space pages:', error);
    return [];
  }
};

/**
 * Trennt Domain von Space
 */
export const disconnectDomainFromSpace = async (userId, spaceId, domain) => {
  try {
    const db = getDatabase();
    const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
    
    // Lösche Domain-Mapping
    const escapedDomain = escapeDomainForFirebase(normalizedDomain);
    const domainRef = ref(db, `/domainMappings/${escapedDomain}`);
    await set(domainRef, null);
    
    // Update Space - entferne Domain-Info
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    const spaceSnapshot = await get(spaceRef);
    
    if (spaceSnapshot.exists()) {
      const spaceData = spaceSnapshot.val();
      const { customDomain, domainStatus, ...spaceWithoutDomain } = spaceData;
      
      await set(spaceRef, {
        ...spaceWithoutDomain,
        updatedAt: new Date().toISOString()
      });
    }
    
    console.log('✅ Domain disconnected from space:', { domain: normalizedDomain, spaceId });
    
  } catch (error) {
    console.error('Error disconnecting domain from space:', error);
    throw error;
  }
};

/**
 * Lädt Cloudflare Domain-Mapping für eine Domain
 */
export const getCloudflareDomainMapping = async (domain) => {
  try {
    const db = getDatabase();
    const escapedDomain = escapeDomainForFirebase(domain);
    const cloudflareRef = ref(db, `/cloudflare/mappings/${escapedDomain}`);
    const snapshot = await get(cloudflareRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      console.log('🔍 Found Cloudflare mapping for domain:', domain, data);
      return {
        ...data,
        originalDomain: domain,
        proxyEnabled: true,
        status: 'active'
      };
    }
    return null;
  } catch (error) {
    console.error('Error loading Cloudflare domain mapping:', error);
    return null;
  }
};

/**
 * EINFACHE DIREKTE DB-QUERY: Finde Domain für User/Space
 */
export const getSpaceDomain = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    
    console.log('🔍 [getSpaceDomain] Searching for domain:', { userId, spaceId });
    
    // 1. Direkte Query: Alle Cloudflare-Mappings durchsuchen
    console.log('🔍 [getSpaceDomain] Checking /cloudflare/mappings...');
    const cloudflareRef = ref(db, '/cloudflare/mappings');
    const cloudflareSnapshot = await get(cloudflareRef);
    
    if (cloudflareSnapshot.exists()) {
      const allMappings = cloudflareSnapshot.val();
      console.log('🔍 [getSpaceDomain] Found Cloudflare mappings:', Object.keys(allMappings).length);
      
      for (const escapedDomain of Object.keys(allMappings)) {
        const mapping = allMappings[escapedDomain];
        console.log('🔍 [getSpaceDomain] Checking mapping:', { 
          escapedDomain, 
          mappingUserId: mapping.userId, 
          mappingSpaceId: mapping.spaceId,
          targetUserId: userId,
          targetSpaceId: spaceId
        });
        
        if (mapping.userId === userId && mapping.spaceId === spaceId) {
          const originalDomain = unescapeDomainFromFirebase(escapedDomain);
          console.log('✅ [getSpaceDomain] FOUND MATCH in Cloudflare mappings:', originalDomain);
          
          return {
            domain: originalDomain,
            domainStatus: 'active',
            mapping: {
              ...mapping,
              originalDomain,
              proxyEnabled: true,
              status: 'active'
            }
          };
        }
      }
    }
    
    // 2. Fallback: Legacy domainMappings durchsuchen
    console.log('🔍 [getSpaceDomain] Checking /domainMappings...');
    const legacyRef = ref(db, '/domainMappings');
    const legacySnapshot = await get(legacyRef);
    
    if (legacySnapshot.exists()) {
      const allMappings = legacySnapshot.val();
      console.log('🔍 [getSpaceDomain] Found legacy mappings:', Object.keys(allMappings).length);
      
      for (const escapedDomain of Object.keys(allMappings)) {
        const mapping = allMappings[escapedDomain];
        console.log('🔍 [getSpaceDomain] Checking legacy mapping:', { 
          escapedDomain, 
          mappingUserId: mapping.userId, 
          mappingSpaceId: mapping.spaceId,
          targetUserId: userId,
          targetSpaceId: spaceId
        });
        
        if (mapping.userId === userId && mapping.spaceId === spaceId) {
          const originalDomain = mapping.domain || unescapeDomainFromFirebase(escapedDomain);
          console.log('✅ [getSpaceDomain] FOUND MATCH in legacy mappings:', originalDomain);
          
          return {
            domain: originalDomain,
            domainStatus: mapping.domainStatus || 'pending',
            mapping: {
              ...mapping,
              originalDomain,
              proxyEnabled: false,
              status: mapping.domainStatus || 'pending'
            }
          };
        }
      }
    }
    
    console.log('❌ [getSpaceDomain] No domain mapping found for this user/space combination');
    return null;
    
  } catch (error) {
    console.error('❌ [getSpaceDomain] Error:', error);
    return null;
  }
};

/**
 * CLOUDFLARE PROXY INTEGRATION - Unlimited Custom Domains
 */

/**
 * Registriert Domain mit Cloudflare Proxy System
 */
export const registerDomainWithProxy = async (domain, userId, username, spaceId) => {
  try {
    const functions = getFunctions();
    const updateCloudflareMapping = httpsCallable(functions, 'updateCloudflareMapping');
    
    console.log('🔄 Registering domain with Cloudflare proxy:', { domain, userId, username, spaceId });
    
    const result = await updateCloudflareMapping({
      domain,
      userId,
      username,
      spaceId,
      action: 'add'
    });
    
    console.log('✅ Domain registered with proxy:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('❌ Failed to register domain with proxy:', error);
    throw error;
  }
};

/**
 * Entfernt Domain aus Cloudflare Proxy System
 */
export const removeDomainFromProxy = async (domain, userId, username, spaceId) => {
  try {
    const functions = getFunctions();
    const updateCloudflareMapping = httpsCallable(functions, 'updateCloudflareMapping');
    
    console.log('🔄 Removing domain from Cloudflare proxy:', { domain, userId, username, spaceId });
    
    const result = await updateCloudflareMapping({
      domain,
      userId,
      username,
      spaceId,
      action: 'remove'
    });
    
    console.log('✅ Domain removed from proxy:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('❌ Failed to remove domain from proxy:', error);
    throw error;
  }
};

/**
 * Erweiterte connectDomainToSpace mit Cloudflare Proxy Integration
 */
export const connectDomainToSpaceWithProxy = async (userId, spaceId, domain, spaceName, username) => {
  try {
    console.log('🚀 Connecting domain to space with Cloudflare proxy:', { userId, spaceId, domain, spaceName, username });
    
    // 1. Erstelle lokales Domain-Mapping (bestehende Logik)
    const domainMapping = await connectDomainToSpace(userId, spaceId, domain, spaceName, username);
    
    // 2. Registriere mit Cloudflare Proxy für unlimited domains
    const proxyResult = await registerDomainWithProxy(domain, userId, username, spaceId);
    
    // 3. Update Domain-Status mit Proxy-Info
    const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
    await updateDomainMapping(normalizedDomain, {
      domainStatus: 'proxy-pending',
      proxyEnabled: true,
      proxyUrl: proxyResult.proxyUrl,
      proxyInstructions: proxyResult.instructions,
      updatedAt: new Date().toISOString()
    });
    
    console.log('✅ Domain connected with proxy support:', { domain: normalizedDomain, proxyEnabled: true });
    
    return {
      ...domainMapping,
      proxyEnabled: true,
      proxyUrl: proxyResult.proxyUrl,
      instructions: proxyResult.instructions,
      message: 'Domain connected with unlimited proxy system! 🚀'
    };
    
  } catch (error) {
    console.error('❌ Error connecting domain with proxy:', error);
    
    // Fallback: Versuche normale Verbindung ohne Proxy
    try {
      console.log('⚡ Fallback: Connecting domain without proxy...');
      const fallbackMapping = await connectDomainToSpace(userId, spaceId, domain, spaceName, username);
      
      return {
        ...fallbackMapping,
        proxyEnabled: false,
        warning: 'Domain connected without proxy. Limited to Firebase hosting domains.',
        instructions: {
          dns: [
            {
              type: 'CNAME',
              name: domain.startsWith('www.') ? 'www' : '@',
              value: 'bonusclub-dev.web.app',
              ttl: 300,
              description: 'Direct Firebase hosting (limited domains)'
            }
          ],
          status: 'Proxy connection failed, using Firebase hosting fallback'
        }
      };
    } catch (fallbackError) {
      console.error('❌ Fallback connection also failed:', fallbackError);
      throw new Error(`Domain connection failed: ${error.message}`);
    }
  }
};

/**
 * Erweiterte disconnectDomainFromSpace mit Proxy-Cleanup
 */
export const disconnectDomainFromSpaceWithProxy = async (userId, spaceId, domain) => {
  try {
    const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
    console.log('🔄 Disconnecting domain from space with proxy cleanup:', { userId, spaceId, domain: normalizedDomain });
    
    // 1. Lade Domain-Info um zu prüfen ob Proxy aktiv ist
    const domainMapping = await getDomainMapping(normalizedDomain);
    
    // 2. Entferne aus Cloudflare Proxy falls aktiv
    if (domainMapping && domainMapping.proxyEnabled) {
      try {
        await removeDomainFromProxy(normalizedDomain, userId, domainMapping.username, spaceId);
        console.log('✅ Domain removed from Cloudflare proxy');
      } catch (proxyError) {
        console.warn('⚠️ Failed to remove domain from proxy:', proxyError.message);
        // Continue with local cleanup even if proxy removal fails
      }
    }
    
    // 3. Standard Domain-Trennung
    await disconnectDomainFromSpace(userId, spaceId, normalizedDomain);
    
    console.log('✅ Domain completely disconnected:', { domain: normalizedDomain, spaceId });
    
  } catch (error) {
    console.error('❌ Error disconnecting domain with proxy:', error);
    throw error;
  }
};

/**
 * Testet Cloudflare Worker Verbindung
 */
export const testCloudflareWorker = async () => {
  // Development Mode Detection
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isDevelopment) {
    console.log('🔧 Development Mode: Assuming Cloudflare Worker is available');
    // In development, nehmen wir einfach an, dass der Worker funktioniert
    // um CORS-Probleme zu vermeiden
    return {
      success: true,
      status: 200,
      workerUrl: 'https://bonusclub-proxy.bonusclub.workers.dev',
      response: '🚀 BonusClub Proxy Worker is running! (Development Mode)',
      method: 'development-mock',
      timestamp: new Date().toISOString(),
      note: 'Worker test skipped in development mode to avoid CORS issues'
    };
  }
  
  try {
    // Production: Versuche zuerst direkten Worker-Test
    const workerUrl = 'https://bonusclub-proxy.bonusclub.workers.dev';
    
    const response = await fetch(workerUrl, {
      method: 'GET',
      headers: {
        'User-Agent': 'BonusClub-Direct-Test'
      }
    });
    
    const text = await response.text();
    
    if (response.ok) {
      return {
        success: true,
        status: response.status,
        workerUrl,
        response: text.substring(0, 200),
        method: 'direct',
        timestamp: new Date().toISOString()
      };
    } else {
      throw new Error(`Worker responded with status ${response.status}`);
    }
    
  } catch (directError) {
    console.warn('⚠️ Direct worker test failed, trying Firebase function:', directError.message);
    
    // Fallback: Firebase function
    try {
      const functions = getFunctions();
      const testWorker = httpsCallable(functions, 'testCloudflareWorker');
      
      const result = await testWorker({});
      return {
        ...result.data,
        method: 'firebase-function'
      };
      
    } catch (firebaseError) {
      console.error('❌ All worker test methods failed');
      return {
        success: false,
        error: `Direct: ${directError.message}, Firebase: ${firebaseError.message}`,
        timestamp: new Date().toISOString(),
        method: 'all-failed'
      };
    }
  }
};

/**
 * Lädt User-spezifische Cloudflare Mappings
 */
export const getUserCloudflareMapping = async (userId) => {
  try {
    const functions = getFunctions();
    const getUserMapping = httpsCallable(functions, 'getUserCloudflareMapping');
    
    const result = await getUserMapping({ userId });
    return result.data || [];
    
  } catch (error) {
    console.error('❌ Failed to load user Cloudflare mappings:', error);
    return [];
  }
};

/**
 * Generiert DNS-Instructions für Cloudflare Proxy
 */
export const generateProxyDNSInstructions = (domain, workerUrl = 'bonusclub-proxy.bonusclub.workers.dev') => {
  const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '');
  const isWwwDomain = normalizedDomain.startsWith('www.');
  const rootDomain = isWwwDomain ? normalizedDomain.substring(4) : normalizedDomain;
  
  // Generiere DNS-Records basierend auf Domain-Typ
  const dnsRecords = [];
  
  if (isWwwDomain) {
    // Für www.example.com - CNAME Record
    dnsRecords.push({
      type: 'CNAME',
      name: 'www',
      value: workerUrl,
      ttl: 300,
      description: 'Points www subdomain to BonusClub proxy system',
      priority: 'Required'
    });
    
    // Optional: Root-Domain weiterleitung
    dnsRecords.push({
      type: 'A',
      name: '@',
      value: '192.0.2.1',
      ttl: 300,
      description: 'Optional: Redirect root domain to www (placeholder IP)',
      priority: 'Optional'
    });
  } else {
    // Für example.com (Root-Domain) - A Record (da CNAME auf Root nicht erlaubt)
    dnsRecords.push({
      type: 'A',
      name: '@',
      value: '104.21.0.0',
      ttl: 300,
      description: 'Points root domain to Cloudflare proxy IP',
      priority: 'Required'
    });
    
    // Zusätzlicher A-Record für Redundanz
    dnsRecords.push({
      type: 'A',
      name: '@',
      value: '172.67.0.0',
      ttl: 300,
      description: 'Secondary Cloudflare proxy IP for redundancy',
      priority: 'Recommended'
    });
    
    // Optional: www-Subdomain auch unterstützen
    dnsRecords.push({
      type: 'CNAME',
      name: 'www',
      value: workerUrl,
      ttl: 300,
      description: 'Optional: Also support www subdomain',
      priority: 'Optional'
    });
  }
  
  return {
    domain: normalizedDomain,
    domainType: isWwwDomain ? 'Subdomain (www)' : 'Root Domain',
    dns: dnsRecords,
    benefits: [
      '🚀 Unlimited custom domains (no Firebase limits)',
      '⚡ Global CDN performance (200+ locations)',
      '🛡️ DDoS protection included',
      '🔒 Automatic SSL certificates',
      '📊 Advanced analytics available',
      '💰 Cost-effective: $0.005 per domain per month'
    ],
    instructions: [
      '1. Log in to your domain provider (GoDaddy, Namecheap, Cloudflare, etc.)',
      '2. Navigate to DNS management section',
      '3. Add the required DNS records shown below',
      '4. Set TTL to 300 seconds (5 minutes) for faster updates',
      '5. Save your changes',
      '6. Wait 2-5 minutes for DNS propagation',
      '7. Test your domain - it will be live automatically! 🎉'
    ],
    testUrl: `https://${normalizedDomain}/to/`,
    urlStructure: {
      info: 'Your pages will be available under the /to/ prefix to avoid conflicts with app routes',
      examples: [
        `https://${normalizedDomain}/to/username/ (Home page)`,
        `https://${normalizedDomain}/to/username/about (About page)`,
        `https://${normalizedDomain}/to/username/contact (Contact page)`
      ]
    },
    estimatedTime: '2-5 minutes for DNS propagation',
    troubleshooting: [
      'If domain doesn\'t work after 10 minutes, check DNS records are correct',
      'Some providers take up to 24 hours for full propagation',
      'Use online DNS checker tools to verify propagation',
      'Contact support if issues persist after 24 hours'
    ]
  };
};
